import { Formik, Form } from "formik";

import { VStack, Input, InputPassword, Button, Field, Link, Heading, toaster } from "@libs/design";
import { useNavigate } from "@libs/router";
import { StorageService } from "@libs/storage-service";
import type { ApiResponse } from "@libs/service-providers/api-service-provider/types";

import { usePostLogin } from "../api/usePostLogin";
import type { LoginData } from "../api/types";

import { useLoginValidation } from "./validations";

type LoginFormValues = {
  email: string;
  password: string;
};

export const Login = () => {
  const { login } = useLoginValidation();
  const navigate = useNavigate();

  const { mutate, isLoading } = usePostLogin({
    onSuccess: (res: ApiResponse<LoginData>) => {
      const { accessToken, user } = res.data ?? {};
      if (!accessToken) return;

      StorageService.set("accessToken", accessToken);
      if (user) StorageService.set("currentUser", user);

      toaster.create({
        description: "Logged in successfully",
        type: "success",
      });
      navigate("/dashboard", { replace: true });
    },
    onError: () => {
      toaster.create({
        description: "Invalid email or password",
        type: "error",
      });
    },
  });

  const initialValues: LoginFormValues = {
    email: "",
    password: "",
  };

  const handleSubmit = (values: LoginFormValues) => {
    mutate(values);
  };

  return (
    <Formik initialValues={initialValues} validationSchema={login} onSubmit={handleSubmit}>
      {({ values, errors, touched, setFieldValue }) => {
        const emailInvalid = !!errors.email && !!touched.email;
        const passwordInvalid = !!errors.password && !!touched.password;

        return (
          <Form style={{ width: "100%" }}>
            <VStack gap={{ base: 4, md: 6 }} w="full">
              {/* Email */}
              <Field.Root invalid={emailInvalid} w="full">
                <Heading size={{ base: "sm", md: "md" }} fontWeight="medium">
                  Email Address
                </Heading>
                <Input
                  placeholder="Please enter your email"
                  type="email"
                  value={values.email}
                  onChange={(e) => setFieldValue("email", e.target.value)}
                />
                {emailInvalid && <Field.ErrorText>{errors.email}</Field.ErrorText>}
              </Field.Root>

              {/* Password */}
              <Field.Root invalid={passwordInvalid} w="full">
                <Heading size={{ base: "sm", md: "md" }} fontWeight="medium">
                  Password
                </Heading>
                <InputPassword
                  placeholder="Please enter your password"
                  py={3}
                  value={values.password}
                  onChange={(e) => setFieldValue("password", e.target.value)}
                />
                {passwordInvalid && <Field.ErrorText>{errors.password}</Field.ErrorText>}
              </Field.Root>

              <Button
                w="full"
                colorScheme="green"
                type="submit"
                size={{ base: "lg", md: "xl" }}
                loading={isLoading}
              >
                Sign In
              </Button>

              <Link alignSelf="center" color="green.500">
                Forgot Password?
              </Link>
            </VStack>
          </Form>
        );
      }}
    </Formik>
  );
};
